<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteXProfile() {
  return {
    name: RouteNames.X_PROFILE
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "@/components/BreadCrumbs.vue";
import { computed, ref } from "vue";
import {
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Trash as TrashIcon
  //Telegram as TelegramIcon
} from "lucide-vue-next";
import BasicCard from "@/components/BasicCard.vue";
import {usePersonStore} from "@/store";
import { storeToRefs } from "pinia";

const passwords = ref({
  current: "",
  new: "",
  confirm: ""
});

const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const changePassword = () => {
  // Implement password change logic
  console.log("Password change requested", passwords.value);
};

const resetForm = () => {
  passwords.value = {
    current: "",
    new: "",
    confirm: ""
  };
  showCurrentPassword.value = false;
  showNewPassword.value = false;
  showConfirmPassword.value = false;
};

const confirmDelete = () => {
  // Implement account deletion confirmation
  console.log("Delete account requested");
};

const personStore = usePersonStore();
const { person } = storeToRefs(personStore);
const profile = computed(() => person.value!.profile);
</script>

<template>
  <BreadCrumbs :title="$t('Profile')" class="mb-5"></BreadCrumbs>

  <div class="space-y-6">
    <!-- Personal Information -->
    <BasicCard title="Personal Information" v-if="person !== null">
      <div class="space-y-4">
        <div class="flex flex-col items-center md:items-start gap-4">
          <div class="w-full">
            <h3 class="text-lg font-medium text-zinc-800">{{ profile?.firstName }} {{ profile?.lastName }}</h3>
          </div>
        </div>

        <!-- Contact Details -->
        <div class="space-y-4 mt-6">
          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700">Phone Number</label>
            <input
              type="text"
              v-model="person.phone"
              class="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-600"
              readonly
            />
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              v-model="person.email"
              class="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-600"
              readonly
            />
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700">Address</label>
            <input
              type="text"
              :value="(profile as any)?.address"
              class="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-600"
              readonly
            />
          </div>
        </div>
      </div>
    </BasicCard>

    <!-- Password Section -->
    <BasicCard title="Change Password">
      <form @submit.prevent="changePassword" class="space-y-4">
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">Current Password</label>
          <div class="relative">
            <input
              :type="showCurrentPassword ? 'text' : 'password'"
              v-model="passwords.current"
              class="w-full p-3 bg-gray-50 rounded-lg pr-10 border border-gray-200"
              placeholder="Enter current password"
            />
            <button
              type="button"
              @click="showCurrentPassword = !showCurrentPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <eye-icon v-if="!showCurrentPassword" class="h-5 w-5 text-gray-400" />
              <eye-off-icon v-else class="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">New Password</label>
          <div class="relative">
            <input
              :type="showNewPassword ? 'text' : 'password'"
              v-model="passwords.new"
              class="w-full p-3 bg-gray-50 rounded-lg pr-10 border border-gray-200"
              placeholder="Enter new password"
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <eye-icon v-if="!showNewPassword" class="h-5 w-5 text-gray-400" />
              <eye-off-icon v-else class="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">Confirm New Password</label>
          <div class="relative">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="passwords.confirm"
              class="w-full p-3 bg-gray-50 rounded-lg pr-10 border border-gray-200"
              placeholder="Confirm new password"
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <eye-icon v-if="!showConfirmPassword" class="h-5 w-5 text-gray-400" />
              <eye-off-icon v-else class="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div class="flex flex-col md:flex-row justify-end gap-3 pt-4">
          <button
            type="button"
            class="w-full md:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            @click="resetForm"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="w-full md:w-auto px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Save Password
          </button>
        </div>
      </form>
    </BasicCard>

    <!-- Account Section -->
    <BasicCard title="Account Settings">
      <div class="space-y-4">
        <p class="text-sm text-gray-600">Manage your account settings and preferences.</p>
        <button
          class="w-full md:w-auto px-4 py-3 text-red-500 bg-red-50 rounded-lg flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
          @click="confirmDelete"
        >
          <trash-icon class="h-5 w-5" />
          Delete Account
        </button>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss"></style>
